<template>
  <transition name="fade">
    <div v-if="data" class="formula-preview-overlay" @click="$emit('close')">
      <div class="formula-preview-content" @click.stop>
        <h3 class="preview-title">{{ $t('formulaPreviewModal.title') }}</h3>
        <div class="preview-formula-container" v-html="renderedFormula"></div>
        <div class="preview-actions">
          <button class="copy-formula-btn" :class="{ 'copied': isCopied }" @click="copyRawFormula">
            <svg v-if="!isCopied" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <span>{{ isCopied ? $t('formulaPreviewModal.copied') : $t('formulaPreviewModal.copyRawFormula') }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MarkdownIt from 'markdown-it';
import mathjax3 from 'markdown-it-mathjax3';

// Shared instance for math rendering
const md = new MarkdownIt().use(mathjax3);

export default {
  name: 'FormulaPreviewModal',
  props: {
    data: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isCopied: false
    };
  },
  computed: {
    renderedFormula() {
      if (!this.data || !this.data.rawLaTeX) return '';
      try {
        // Wrap LaTeX with $$ for block display
        const rendered = md.render(`$$${this.data.rawLaTeX}$$`);
        // Fallback if rendering seems to have failed
        if (rendered.length < 20) {
           return `<div class="raw-latex-fallback">${this.data.rawLaTeX}</div>`;
        }
        return rendered;
      } catch (e) {
        console.error('MathJax render error:', e);
        return `<div class="error-msg">${this.$t('formulaPreviewModal.renderError')}</div><div class="raw-latex-fallback">${this.data.rawLaTeX}</div>`;
      }
    }
  },
  methods: {
    async copyRawFormula() {
      if (!this.data || !this.data.rawLaTeX) return;
      const text = this.data.rawLaTeX;
      
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          this.onCopySuccess();
        } else {
          throw new Error('Clipboard API not available');
        }
      } catch (err) {
        // Fallback for non-secure contexts or older browsers
        try {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-9999px";
          textArea.style.top = "0";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          if (successful) {
            this.onCopySuccess();
          } else {
            throw new Error('Fallback copy failed');
          }
        } catch (fallbackErr) {
          console.error('All copy methods failed:', fallbackErr);
        }
      }
    },
    onCopySuccess() {
      this.isCopied = true;
      setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    }
  }
};
</script>

<style scoped>
.formula-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
}

.formula-preview-content {
  position: relative;
  max-width: 90%;
  max-height: 80%;
  background: var(--bg-surface, #2d2d2d);
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  cursor: default;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.preview-title {
  margin: 0;
  color: var(--text-primary, #fff);
  font-size: 1.2rem;
  font-weight: 600;
}

.preview-formula-container {
  min-width: 200px;
  min-height: 100px;
  max-width: 100%;
  max-height: 60vh;
  overflow-y: auto;
  padding: 1rem;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff; /* Explicit white color for better contrast */
  background: #1a1a1a; /* Explicit dark background */
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.2);
}

.preview-formula-container ::v-deep mjx-container {
  margin: 0 !important;
  font-size: 1.5em !important;
  color: white !important;
}

.preview-formula-container ::v-deep svg {
  fill: white !important;
  color: white !important;
}

.raw-latex-fallback {
  font-family: monospace;
  word-break: break-all;
  padding: 10px;
  color: #ccc;
}

.error-msg {
  color: #ff4d4d;
  margin-bottom: 10px;
}

.preview-actions {
  display: flex;
  justify-content: center;
  width: 100%;
}

.copy-formula-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.copy-formula-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 122, 255, 0.4);
  filter: brightness(1.1);
}

.copy-formula-btn.copied {
  background: #34c759;
  box-shadow: 0 4px 12px rgba(52, 199, 89, 0.3);
}

.copy-formula-btn:active {
  transform: translateY(0);
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
